import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';
import {
  EmptyState,
  ErrorBox,
  Loader,
  PageHeader,
  StatusBadge,
  formatDateTime,
} from '../../components/Common';
import type { Booking } from '../../types';

export function MasterCalendarPage() {
  const [offsetDays, setOffsetDays] = useState(0);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['master-bookings'],
    queryFn: () => api.bookings.forMaster(false),
  });

  const grouped = useMemo(() => groupByDate(data ?? []), [data]);
  const dates = Object.keys(grouped).sort();
  const selected = dates[offsetDays];

  return (
    <div>
      <PageHeader
        title="Календарь"
        subtitle="Активные записи (pending + confirmed + tentative)"
      />

      {isLoading && <Loader />}
      {error && <ErrorBox message={(error as Error).message} onRetry={() => void refetch()} />}

      {!isLoading && dates.length === 0 && (
        <EmptyState icon="📅" title="Нет активных записей" />
      )}

      {dates.length > 0 && selected && (
        <>
          <div className="mb-3 flex items-center justify-between">
            <button
              className="btn-secondary"
              disabled={offsetDays === 0}
              onClick={() => setOffsetDays((d) => Math.max(0, d - 1))}
            >
              ‹
            </button>
            <p className="font-semibold">
              {new Date(selected).toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: 'long',
                weekday: 'short',
              })}
            </p>
            <button
              className="btn-secondary"
              disabled={offsetDays >= dates.length - 1}
              onClick={() => setOffsetDays((d) => Math.min(dates.length - 1, d + 1))}
            >
              ›
            </button>
          </div>

          <ul className="space-y-2">
            {grouped[selected].map((b) => (
              <li key={b.id}>
                <Link to={`/bookings/${b.id}`} className="card flex items-center justify-between active:opacity-80">
                  <div>
                    <p className="font-semibold">
                      {new Date(b.slot_start).toLocaleTimeString('ru-RU', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {' — '}
                      {b.service?.name ?? 'Услуга'}
                    </p>
                    <p className="text-xs text-tg-hint">
                      Клиент: {b.client?.first_name ?? ''}
                    </p>
                    <p className="text-xs text-tg-hint">{formatDateTime(b.slot_end, { timeOnly: true })} (конец)</p>
                  </div>
                  <StatusBadge status={b.status} />
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function groupByDate(bookings: Booking[]): Record<string, Booking[]> {
  const out: Record<string, Booking[]> = {};
  for (const b of bookings) {
    const key = new Date(b.slot_start).toISOString().slice(0, 10);
    out[key] = out[key] ?? [];
    out[key].push(b);
  }
  for (const k of Object.keys(out)) {
    out[k].sort((a, b) => a.slot_start.localeCompare(b.slot_start));
  }
  return out;
}
